// Partner Coaching Content - Hand-written tips and guidance
// No AI - all content carefully crafted for partners
//
// REFERENCES & SOURCES:
// All information is based on peer-reviewed research and expert guidelines.
//
// Primary Sources:
// - American College of Obstetricians and Gynecologists (ACOG) - www.acog.org
// - March of Dimes - www.marchofdimes.org/partners
// - Postpartum Support International - www.postpartum.net
// - The Gottman Institute (relationship research) - www.gottman.com
// - "The Expectant Father" by Armin Brott (evidence-based partner guide)
// - "The Birth Partner" by Penny Simkin (childbirth support guide)
//
// Key Guidelines Referenced:
// - ACOG Patient Education: "For Fathers and Partners"
// - ACOG FAQ: "Support During Pregnancy and Labor"
// - WHO Guidelines on Companion of Choice During Labour
// - AAP Guidelines on Parental Involvement
//
// Relationship & Mental Health Sources:
// - Gottman Institute research on transitions to parenthood
// - Postpartum Support International partner resources
// - NIH research on paternal perinatal mental health
//
// Disclaimer: This content is for informational purposes only and does not
// constitute medical or psychological advice. Support your partner's medical
// team and seek professional help for mental health concerns.

export interface WeeklyPartnerTip {
    week: number;
    title: string;
    mainTip: string;
    doThis: string[];
    sayThis: string;
    selfCare: string;
}

export interface SupportScript {
    situation: string;
    whatToSay: string[];
    whatNotToSay: string[];
    actionToTake: string;
}

export interface PracticalTask {
    category: string;
    tasks: { task: string; timing: string; }[];
}

// Weekly tips for partners - one per week of pregnancy
export const WEEKLY_PARTNER_TIPS: WeeklyPartnerTip[] = [
    {
        week: 4,
        title: "The Big News",
        mainTip: "She just found out. Your reaction matters more than you know. Be present, be excited, be honest about your feelings too.",
        doThis: [
            "Put down your phone and be fully present",
            "Ask how she's feeling before sharing your own reaction",
            "Start a private photo or journal together"
        ],
        sayThis: "I'm so happy we're doing this together. How are you feeling?",
        selfCare: "It's okay to feel scared alongside excited. Both feelings are valid."
    },
    {
        week: 5,
        title: "Early Days",
        mainTip: "Morning sickness might start. It can happen any time of day. Stock up on crackers, ginger ale, and patience.",
        doThis: [
            "Keep saltines by the bed for morning nausea",
            "Take over cooking if smells bother her",
            "Don't take it personally if she's irritable"
        ],
        sayThis: "What sounds good to eat? I'll handle dinner tonight.",
        selfCare: "You can't fix morning sickness, but you can make it easier. That's enough."
    },
    {
        week: 6,
        title: "Exhaustion Hits",
        mainTip: "Growing a human is exhausting. She may need 12+ hours of sleep. Don't guilt-trip, don't suggest she's lazy. Just let her rest.",
        doThis: [
            "Handle evening chores so she can sleep early",
            "Don't wake her on weekends",
            "Keep the bedroom cool and dark"
        ],
        sayThis: "Go rest. I've got everything handled out here.",
        selfCare: "Use this time to pick up a hobby or catch up with friends. You need your outlets too."
    },
    {
        week: 7,
        title: "Food Aversions",
        mainTip: "Her favorite foods may now be repulsive. Don't be offended if she can't stand your cooking. Smell sensitivity is intense.",
        doThis: [
            "Ask before cooking anything with strong smells",
            "Eat pungent foods elsewhere",
            "Keep multiple snack options available"
        ],
        sayThis: "What can you tolerate right now? I'll find something that works.",
        selfCare: "Missing your normal meals together? This phase will pass. Be patient with yourself too."
    },
    {
        week: 8,
        title: "First Appointment",
        mainTip: "The first ultrasound is huge. Be there if you possibly can. Seeing that heartbeat together is something you'll never forget.",
        doThis: [
            "Block your calendar - this is non-negotiable",
            "Bring tissues (seriously)",
            "Ask questions - this is your baby too"
        ],
        sayThis: "I want to be at every appointment I can. This is our journey.",
        selfCare: "It's okay if you feel disconnected still. The heartbeat makes it real for many partners."
    },
    {
        week: 9,
        title: "Emotional Rollercoaster",
        mainTip: "Hormones are doing wild things. She might cry at commercials, snap over small things, then apologize five minutes later. Ride the wave.",
        doThis: [
            "Don't try to 'fix' emotional moments",
            "Offer hugs, not solutions",
            "Give space when she needs it"
        ],
        sayThis: "I'm here. You don't have to explain anything.",
        selfCare: "Her mood swings aren't about you. Take a walk if you need a breather."
    },
    {
        week: 10,
        title: "Keeping the Secret",
        mainTip: "You might be keeping pregnancy secret until after the first trimester. This can feel isolating. Find one trusted person to confide in.",
        doThis: [
            "Help deflect awkward questions about drinking",
            "Be her excuse when she needs to leave early",
            "Share the excitement with one trusted friend if needed"
        ],
        sayThis: "I know it's hard keeping this in. I'm so proud of us.",
        selfCare: "Having to hide big news is stressful. Acknowledge that this is hard for you too."
    },
    {
        week: 11,
        title: "Prenatal Vitamins",
        mainTip: "Make sure she's taking prenatals daily. Some cause nausea - help her find ones that work. Gummies are easier for some.",
        doThis: [
            "Set a daily reminder together",
            "Research alternative vitamin options if current ones cause issues",
            "Pick them up before they run out"
        ],
        sayThis: "Let me grab your vitamins when I'm at the store. Need anything else?",
        selfCare: "You can start taking a daily vitamin too. Role model healthy habits."
    },
    {
        week: 12,
        title: "End of First Trimester",
        mainTip: "The riskiest period is ending. If you've been holding your breath, you can exhale a little. Time to start sharing the news!",
        doThis: [
            "Plan how you'll announce together",
            "Discuss who gets told first",
            "Celebrate this milestone - you made it!"
        ],
        sayThis: "We did it. Let's celebrate before we tell everyone.",
        selfCare: "The anxiety of early pregnancy is real. Acknowledge what you've been carrying too."
    },
    {
        week: 13,
        title: "Second Trimester Begins",
        mainTip: "Energy often returns now. Morning sickness may fade. This is often called the 'honeymoon trimester' - enjoy it!",
        doThis: [
            "Plan a date night or weekend trip",
            "Start looking at baby gear together",
            "Take bump photos to document the journey"
        ],
        sayThis: "You seem to have more energy lately. Want to go do something fun?",
        selfCare: "Use this easier period to reconnect. You'll want these memories later."
    },
    {
        week: 14,
        title: "Growing Bump",
        mainTip: "The bump is starting to show. She might feel self-conscious or excited. Follow her lead on how to acknowledge it.",
        doThis: [
            "Compliment her genuinely",
            "Don't comment on size unless she brings it up",
            "Help her shop for comfortable clothes"
        ],
        sayThis: "You look amazing. I love seeing you like this.",
        selfCare: "If you're feeling disconnected from the pregnancy, touching the bump can help."
    },
    {
        week: 15,
        title: "Baby Movement Soon",
        mainTip: "She might start feeling flutters soon. You probably won't feel kicks from outside yet, but ask her to describe it.",
        doThis: [
            "Ask about movements each day",
            "Put your hand on her belly when she feels something",
            "Be patient - you'll feel kicks soon enough"
        ],
        sayThis: "Tell me what it feels like when the baby moves.",
        selfCare: "Feeling left out is normal. The pregnancy is very internal for her right now."
    },
    {
        week: 16,
        title: "Gender Decisions",
        mainTip: "You may find out the sex soon. Discuss ahead of time: do you both want to know? Want a surprise? Make this decision together.",
        doThis: [
            "Have a real conversation about preferences",
            "Plan how you'll share (or not share) with family",
            "Respect each other's feelings about the outcome"
        ],
        sayThis: "Whatever we're having, I'm excited. But how do you feel about finding out?",
        selfCare: "It's okay to have gender preferences. Just be prepared to love whoever arrives."
    },
    {
        week: 17,
        title: "Aches and Pains",
        mainTip: "Back pain, round ligament pain, and other discomforts are starting. Offer massages, heating pads, and understanding.",
        doThis: [
            "Learn where round ligament pain occurs (lower belly sides)",
            "Keep a heating pad accessible",
            "Offer foot rubs without being asked"
        ],
        sayThis: "Can I rub your back? You've been on your feet a lot today.",
        selfCare: "Watching her uncomfortable and not being able to fix it is hard. Your presence helps more than you know."
    },
    {
        week: 18,
        title: "Anatomy Scan Coming",
        mainTip: "The 20-week anatomy scan is the big one. This is when they check everything. Be there, be present, be ready for detailed images.",
        doThis: [
            "Clear your schedule for this appointment",
            "Research what they'll be looking at",
            "Prepare questions for the technician"
        ],
        sayThis: "I wouldn't miss this for anything. What do you want to ask them?",
        selfCare: "This scan can bring up anxiety. It's okay to feel nervous about the results."
    },
    {
        week: 19,
        title: "Nesting Instincts",
        mainTip: "She might start wanting to organize, clean, and prepare the home. This is biological. Help her, don't tease her about it.",
        doThis: [
            "Offer to help with projects",
            "Don't dismiss 'nesting' as silly",
            "Start looking at nursery ideas together"
        ],
        sayThis: "I noticed you've been organizing. What can I help with?",
        selfCare: "You might not feel the same urgency. That's okay. Just support her instincts."
    },
    {
        week: 20,
        title: "Halfway There",
        mainTip: "You're at the halfway point! Celebrate this milestone. Take a 20-week photo, have a special dinner, acknowledge how far you've come.",
        doThis: [
            "Mark this milestone with something special",
            "Write a letter to your future baby",
            "Start a baby registry together"
        ],
        sayThis: "Halfway there. I'm so proud of you and excited for what's coming.",
        selfCare: "Reflect on how you've grown as a partner these past months."
    },
    {
        week: 21,
        title: "Baby Kicks You Can Feel",
        mainTip: "You should be able to feel kicks from outside now! Be patient, don't press too hard, and cherish these moments.",
        doThis: [
            "Put your hand on her belly when baby is active",
            "Talk or sing to the bump - baby can hear you now",
            "Don't get frustrated if baby stops when you touch"
        ],
        sayThis: "Oh wow, I felt that! Hi in there, little one.",
        selfCare: "These moments make it real. Let yourself feel the wonder of it."
    },
    {
        week: 22,
        title: "Baby Names",
        mainTip: "If you haven't started the name conversation, now's a good time. Be open-minded - this is a joint decision.",
        doThis: [
            "Each make a list of 10 names independently",
            "Discuss meanings and family significance",
            "Don't veto without discussion"
        ],
        sayThis: "Let's each come up with 10 names and compare. No judgments.",
        selfCare: "Name discussions can get heated. Remember you're on the same team."
    },
    {
        week: 23,
        title: "Preparing Siblings or Pets",
        mainTip: "If you have other children or pets, start preparing them now. Changes are coming and everyone needs time to adjust.",
        doThis: [
            "Read sibling books with older kids",
            "Start changing pet routines gradually",
            "Discuss how roles might change"
        ],
        sayThis: "Our family is growing. Let's make sure everyone feels included.",
        selfCare: "Managing existing children plus pregnancy is a lot. Give yourself grace."
    },
    {
        week: 24,
        title: "Viability Milestone",
        mainTip: "24 weeks is considered 'viability' - baby could survive outside the womb with medical help. A significant milestone emotionally.",
        doThis: [
            "Acknowledge this milestone together",
            "Discuss any lingering anxieties",
            "Start thinking about childcare options"
        ],
        sayThis: "This feels like a big milestone. How are you feeling about everything?",
        selfCare: "If you've been anxious about pregnancy loss, this can bring relief. Let yourself feel that."
    },
    {
        week: 25,
        title: "Glucose Test Prep",
        mainTip: "The glucose tolerance test is coming up around week 24-28. She'll drink a super sweet drink and wait. Offer to drive and distract her.",
        doThis: [
            "Drive her to the appointment (she may feel off after)",
            "Bring entertainment for the waiting hour",
            "Have a good meal ready for after"
        ],
        sayThis: "I'll drive you and wait with you. What show should we watch together?",
        selfCare: "Being a supportive presence during medical stuff is valuable partner work."
    },
    {
        week: 26,
        title: "Third Trimester Approaching",
        mainTip: "Energy may start dipping again. The third trimester is the home stretch but also the heaviest (literally). Prepare for more support duties.",
        doThis: [
            "Take on more household tasks preemptively",
            "Help her find comfortable sleep positions",
            "Start packing a hospital bag early"
        ],
        sayThis: "Let me handle more around the house. You focus on growing our baby.",
        selfCare: "Your workload is increasing. Make sure you're maintaining your health too."
    },
    {
        week: 27,
        title: "Third Trimester Begins",
        mainTip: "The final stretch! 13 weeks to go. She may be uncomfortable, anxious about delivery, and exhausted. Be her rock.",
        doThis: [
            "Start childbirth classes if you haven't",
            "Review the birth plan together",
            "Practice relaxation techniques with her"
        ],
        sayThis: "We're in the home stretch. What can I do to make these last weeks easier?",
        selfCare: "Third trimester is a marathon for partners too. Pace yourself."
    },
    {
        week: 28,
        title: "Appointments Increase",
        mainTip: "Prenatal visits become more frequent now (every 2 weeks, then weekly). Help with scheduling and try to attend as many as possible.",
        doThis: [
            "Add all appointments to your calendar",
            "Prepare questions for each visit",
            "Track fetal movement together"
        ],
        sayThis: "I've saved all the appointments. Which ones do you want me at?",
        selfCare: "Frequent appointments can feel overwhelming. You don't have to attend every single one."
    },
    {
        week: 29,
        title: "Sleep Is Hard",
        mainTip: "She probably can't sleep well anymore. The bump is big, she's uncomfortable, she has to pee constantly. Be patient with nighttime disruptions.",
        doThis: [
            "Get a pregnancy pillow if you haven't",
            "Don't complain if she tosses and turns",
            "Offer to sleep elsewhere on rough nights (her choice)"
        ],
        sayThis: "I know sleep is hard. Take as many naps as you need.",
        selfCare: "Your sleep is disrupted too. It's okay to admit that and find solutions."
    },
    {
        week: 30,
        title: "Braxton Hicks",
        mainTip: "Practice contractions (Braxton Hicks) may start. They're uncomfortable but normal. Know the signs of real labor vs. practice.",
        doThis: [
            "Learn the difference between Braxton Hicks and real contractions",
            "Time contractions when they seem regular",
            "Have a plan for getting to the hospital"
        ],
        sayThis: "How do those feel? Should we time them or is it settling down?",
        selfCare: "Early labor signs can make you anxious. Know the facts, stay calm."
    },
    {
        week: 31,
        title: "Hospital Bag Time",
        mainTip: "Pack the hospital bag now if you haven't. Include things for you too - snacks, phone charger, change of clothes, entertainment.",
        doThis: [
            "Make a checklist together",
            "Pack comfortable nursing bras and clothes for her",
            "Include car seat installation in your prep"
        ],
        sayThis: "Let's pack the bag this weekend so we're not scrambling.",
        selfCare: "Having logistics handled reduces everyone's anxiety."
    },
    {
        week: 32,
        title: "Baby Shower",
        mainTip: "Baby shower may be happening now. Get involved in thanking guests, assembling gear, and organizing gifts afterward.",
        doThis: [
            "Help write thank you cards",
            "Assemble furniture and gear",
            "Wash and organize baby clothes"
        ],
        sayThis: "I'll handle setting up the nursery furniture. You rest.",
        selfCare: "Shower excitement is lovely but also overwhelming. Take breaks when needed."
    },
    {
        week: 33,
        title: "Birth Class Reminders",
        mainTip: "Review what you learned in birth class. Know the stages of labor, pain management options, and your role as support person.",
        doThis: [
            "Practice breathing techniques together",
            "Know what comfort measures she wants",
            "Understand her pain relief preferences"
        ],
        sayThis: "Let's review the birth plan. I want to be as prepared as possible.",
        selfCare: "Feeling unprepared is normal. The birth team will guide you too."
    },
    {
        week: 34,
        title: "Work Preparations",
        mainTip: "Start wrapping up at work. Prepare your team for your leave, set up out-of-office plans, and mentally transition.",
        doThis: [
            "Finalize your paternity leave plan",
            "Train your coverage person",
            "Clear as many deadlines as possible"
        ],
        sayThis: "I've got work covered. I'll be fully present when baby comes.",
        selfCare: "Transitioning from work to new parenthood is a big mental shift. Start now."
    },
    {
        week: 35,
        title: "Car Seat Installation",
        mainTip: "Install the car seat NOW. Hospital won't let you leave without one. Many fire stations will check your installation for free.",
        doThis: [
            "Install the car seat and have it inspected",
            "Know how to adjust straps for newborn size",
            "Practice buckling the seat without a baby"
        ],
        sayThis: "Car seat is installed and inspected. One less thing to worry about.",
        selfCare: "Checking big items off the list feels good. Celebrate small wins."
    },
    {
        week: 36,
        title: "Weekly Appointments",
        mainTip: "Appointments are weekly now. She's getting checked for dilation. These appointments can be exciting and nerve-wracking.",
        doThis: [
            "Attend as many weekly appointments as you can",
            "Ask about signs of labor to watch for",
            "Finalize your hospital route and backup plan"
        ],
        sayThis: "I'll be at the appointment. Want to grab lunch after?",
        selfCare: "The anticipation is intense. Find healthy ways to manage your own anxiety."
    },
    {
        week: 37,
        title: "Full Term!",
        mainTip: "Baby is considered full term now and could arrive any day. Make sure everything is ready - bag packed, car seat installed, plan in place.",
        doThis: [
            "Keep your phone charged and accessible",
            "Know who to call when labor starts",
            "Have the birth plan easily accessible"
        ],
        sayThis: "We're full term! I'm ready whenever baby is.",
        selfCare: "The waiting game is hard. Stay busy but don't over-schedule."
    },
    {
        week: 38,
        title: "False Alarms",
        mainTip: "You might have false labor scares. Stay calm, time contractions, and follow your provider's guidance on when to go in.",
        doThis: [
            "Don't panic at every twinge",
            "Time contractions: 5-1-1 rule (5 min apart, 1 min long, for 1 hour)",
            "Keep her calm if she's anxious"
        ],
        sayThis: "Let's time these together. No rush, we'll know when it's time.",
        selfCare: "False alarms are exhausting. Give yourself grace if you feel jumpy."
    },
    {
        week: 39,
        title: "Any Day Now",
        mainTip: "Baby could come any moment. Stay close to home, keep your phone on, and don't plan anything you can't cancel.",
        doThis: [
            "Clear your calendar",
            "Keep gas in the car",
            "Double-check the hospital bag"
        ],
        sayThis: "I'm not going anywhere far. We're in this together.",
        selfCare: "The waiting is the hardest part for some. Find ways to stay calm and present."
    },
    {
        week: 40,
        title: "Due Date Week",
        mainTip: "Due date is here! Only 5% of babies arrive on their due date. She may be frustrated, uncomfortable, and ready. Be extra supportive.",
        doThis: [
            "Don't ask 'any baby yet?' - everyone else will",
            "Plan distractions and fun activities",
            "Be patient with her frustration"
        ],
        sayThis: "Baby will come when ready. What can I do to make today better?",
        selfCare: "You're about to become a parent. Take a moment to reflect on this journey."
    }
];

// Emotional support scripts for common situations
export const SUPPORT_SCRIPTS: SupportScript[] = [
    {
        situation: "She's crying and you don't know why",
        whatToSay: [
            "I'm here. You don't have to explain.",
            "Can I just hold you?",
            "Let it out. I'm not going anywhere."
        ],
        whatNotToSay: [
            "Why are you crying?",
            "Is it the hormones?",
            "Calm down, it's not that big a deal."
        ],
        actionToTake: "Just be present. Offer a hug, tissues, water. Don't try to fix it."
    },
    {
        situation: "She's frustrated with her changing body",
        whatToSay: [
            "You're growing our baby. Your body is amazing.",
            "You're beautiful to me, always.",
            "What can I do to help you feel more comfortable?"
        ],
        whatNotToSay: [
            "You'll lose the weight after.",
            "At least it's temporary.",
            "I didn't notice."
        ],
        actionToTake: "Compliment her genuinely and often. Help her find comfortable clothes."
    },
    {
        situation: "She's anxious about something going wrong",
        whatToSay: [
            "I understand why you're worried. Let's talk about it.",
            "What would help you feel better right now?",
            "We'll handle whatever comes, together."
        ],
        whatNotToSay: [
            "Don't worry about it.",
            "You're being paranoid.",
            "The odds are really low, just relax."
        ],
        actionToTake: "Acknowledge her feelings. Offer to call the doctor if she needs reassurance."
    },
    {
        situation: "She snaps at you over something small",
        whatToSay: [
            "I can see you're having a hard moment. I'm going to give you some space.",
            "I love you. Let's talk when you're ready.",
            "(Say nothing, just be present)"
        ],
        whatNotToSay: [
            "What's your problem?",
            "It's just your hormones.",
            "You're being unreasonable."
        ],
        actionToTake: "Don't escalate. Give space, then reconnect with kindness. No grudges."
    },
    {
        situation: "She's scared about labor and delivery",
        whatToSay: [
            "I'll be right there with you the whole time.",
            "The medical team will take care of you. We've got a plan.",
            "Women have been doing this forever. You are strong."
        ],
        whatNotToSay: [
            "It can't be that bad.",
            "My mom said it wasn't a big deal.",
            "Just get the epidural and you'll be fine."
        ],
        actionToTake: "Review the birth plan together. Remind her of her strength and your support."
    },
    {
        situation: "She's overwhelmed by everyone's opinions and advice",
        whatToSay: [
            "We're making our own choices. I support whatever you decide.",
            "You don't have to take their advice. You know what's best.",
            "Want me to help field some of these conversations?"
        ],
        whatNotToSay: [
            "They're just trying to help.",
            "Maybe you should listen to them.",
            "My mom knows what she's talking about."
        ],
        actionToTake: "Be her buffer. Help redirect unwanted advice. Stand united in your decisions."
    },
    {
        situation: "She feels like she's not doing enough to prepare",
        whatToSay: [
            "You're literally growing a human. That's enough.",
            "We're more prepared than you think. Let me show you.",
            "What would make you feel more ready? I'll help."
        ],
        whatNotToSay: [
            "Yeah, we probably should do more.",
            "We still need to...",
            "Other people seem more prepared."
        ],
        actionToTake: "Make a checklist together of what's done vs. left to do. Tackle items together."
    },
    {
        situation: "She's exhausted but can't sleep",
        whatToSay: [
            "I'm sorry you can't sleep. Can I get you anything?",
            "Want me to rub your back until you're drowsy?",
            "Should I put on something boring to help you fall asleep?"
        ],
        whatNotToSay: [
            "You should try to sleep, you need rest.",
            "I'm tired too.",
            "*snoring while she's wide awake*"
        ],
        actionToTake: "Offer comfort measures. Consider sleeping separately so she can toss without guilt."
    }
];

// Practical support tasks organized by trimester
export const PRACTICAL_TASKS: PracticalTask[] = [
    {
        category: "First Trimester (Weeks 1-13)",
        tasks: [
            { task: "Research and schedule prenatal appointments", timing: "Weeks 4-6" },
            { task: "Learn about foods and substances to avoid", timing: "Immediately" },
            { task: "Take over cooking if smells cause nausea", timing: "Weeks 5-12" },
            { task: "Stock the house with nausea-friendly snacks", timing: "Weeks 5-12" },
            { task: "Help her remember daily prenatal vitamins", timing: "Daily" },
            { task: "Handle morning chores so she can rest", timing: "Daily" },
            { task: "Attend first ultrasound appointment", timing: "Week 8" },
            { task: "Discuss when and how to share the news", timing: "Weeks 10-13" }
        ]
    },
    {
        category: "Second Trimester (Weeks 14-26)",
        tasks: [
            { task: "Plan a babymoon trip", timing: "Weeks 14-24" },
            { task: "Start researching baby gear", timing: "Week 16+" },
            { task: "Attend anatomy scan appointment", timing: "Week 20" },
            { task: "Start looking at nursery ideas", timing: "Week 18+" },
            { task: "Help her shop for maternity clothes", timing: "As needed" },
            { task: "Begin discussing baby names", timing: "Week 20+" },
            { task: "Research childcare options", timing: "Week 20+" },
            { task: "Start a baby registry together", timing: "Week 20+" }
        ]
    },
    {
        category: "Third Trimester (Weeks 27-40)",
        tasks: [
            { task: "Attend childbirth preparation classes", timing: "Weeks 28-34" },
            { task: "Create a birth plan together", timing: "Week 28-32" },
            { task: "Pack the hospital bag", timing: "Week 32-35" },
            { task: "Install and inspect car seat", timing: "Week 35" },
            { task: "Assemble nursery furniture", timing: "Week 32-36" },
            { task: "Finalize paternity leave at work", timing: "Week 34+" },
            { task: "Prepare freezer meals for after birth", timing: "Weeks 34-38" },
            { task: "Set up pediatrician appointment", timing: "Week 34+" },
            { task: "Attend weekly prenatal appointments", timing: "Week 36+" },
            { task: "Keep phone charged and car gassed up", timing: "Week 37+" }
        ]
    },
    {
        category: "Ongoing Throughout Pregnancy",
        tasks: [
            { task: "Take weekly bump photos together", timing: "Weekly" },
            { task: "Give foot or back massages", timing: "As needed" },
            { task: "Handle heavy lifting and chores", timing: "Ongoing" },
            { task: "Attend prenatal appointments", timing: "As scheduled" },
            { task: "Drive when she's too uncomfortable", timing: "Third trimester" },
            { task: "Run errands and grocery shopping", timing: "Third trimester" },
            { task: "Keep communication open about feelings", timing: "Ongoing" },
            { task: "Show appreciation and express love", timing: "Daily" }
        ]
    }
];

// Self-care reminders for partners
export const PARTNER_SELF_CARE: string[] = [
    "Your feelings matter too. It's okay to have your own anxieties about becoming a parent.",
    "Find a friend or family member you can talk to honestly about how you're doing.",
    "Maintain at least one hobby or outlet - you'll need healthy coping mechanisms.",
    "Get enough sleep when you can. Sleep becomes precious after baby arrives.",
    "Exercise reduces stress. Keep moving even when life gets busy.",
    "It's normal to feel disconnected from the pregnancy. You're not carrying the baby - that takes time.",
    "Your relationship will change. Invest in it now - date nights, conversations, intimacy.",
    "Don't compare yourself to other partners. Everyone's journey is different.",
    "Ask for help when you need it. You don't have to figure everything out alone.",
    "Take mental health seriously. Anxiety and depression affect partners too."
];

// Communication templates for difficult conversations
export const COMMUNICATION_TIPS: { topic: string; approach: string; example: string }[] = [
    {
        topic: "Finances and budgeting for baby",
        approach: "Approach as a team problem, not accusatory. Use 'we' language.",
        example: "I'd love to sit down and look at our budget together. What do we need to adjust for baby expenses?"
    },
    {
        topic: "Parenting philosophy differences",
        approach: "Listen first, share opinions second. Find common ground.",
        example: "Tell me more about how you were raised. I want to understand where you're coming from."
    },
    {
        topic: "Division of labor after baby",
        approach: "Be specific and flexible. Expectations should be explicit.",
        example: "Can we make a plan for who does what in those first weeks? I want to make sure we're on the same page."
    },
    {
        topic: "Involving family and boundaries",
        approach: "Present as united front. Discuss privately first.",
        example: "I want to make sure we're aligned on boundaries with family. What do you need me to handle?"
    },
    {
        topic: "Return to work discussions",
        approach: "No judgment either direction. Explore options together.",
        example: "What are you thinking about work after baby? I want to support whatever makes sense for our family."
    },
    {
        topic: "Intimacy changes during pregnancy",
        approach: "Follow her lead completely. Express love without pressure.",
        example: "I love you regardless. Tell me what feels good right now."
    }
];

// Helper function to get tip for current week
export function getPartnerTipForWeek(week: number): WeeklyPartnerTip | null {
    if (week < 4) return WEEKLY_PARTNER_TIPS[0]; // Default to week 4 for early access
    if (week > 40) return WEEKLY_PARTNER_TIPS[WEEKLY_PARTNER_TIPS.length - 1]; // Cap at 40
    return WEEKLY_PARTNER_TIPS.find(tip => tip.week === week) || null;
}
